import { useCallback, useState } from "react";
import { useUserRole } from "@/hooks/useUserRole";
import { resetBankLink } from "@/lib/api/bankService";
import { showApiError, showSuccess } from "@/lib/utils/notifications";
import { APPLICATION_BANK_LINK_RESET_POLICY } from "../../../../constants";
import { shouldShowResetBankLinkButton } from "../../../../utils/creditReportHelpers";

export function useResetBankLink(options: {
  applicantId: string;
  applicationStatus?: string | null;
  hasBankLinkingSteps?: boolean;
  onRefresh?: () => void | Promise<void>;
  refreshAccounts?: () => void | Promise<void>;
}) {
  const {
    applicantId,
    applicationStatus,
    hasBankLinkingSteps = false,
    onRefresh,
    refreshAccounts,
  } = options;

  const { hasPolicy } = useUserRole();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const showResetBankLink = shouldShowResetBankLinkButton({
    hasBankLinkResetPolicy: hasPolicy([APPLICATION_BANK_LINK_RESET_POLICY]),
    applicationStatus,
    hasBankLinkingSteps,
  });

  const openConfirm = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const performReset = useCallback(async () => {
    setConfirmOpen(false);
    if (!applicantId?.trim() || processing) return;
    setProcessing(true);
    try {
      const response = await resetBankLink(applicantId);
      const msg =
        typeof response?.message === "string" && response.message.trim()
          ? response.message
          : "Bank linking has been reset. The applicant can attempt bank linking again.";
      showSuccess(msg);
      if (refreshAccounts) await refreshAccounts();
      if (onRefresh) await onRefresh();
    } catch (error: unknown) {
      showApiError(error);
    } finally {
      setProcessing(false);
    }
  }, [applicantId, onRefresh, processing, refreshAccounts]);

  return {
    showResetBankLink,
    processing,
    confirmOpen,
    openConfirm,
    closeConfirm,
    performReset,
  };
}
